import React, { Component } from 'react';
import PropTypes from 'prop-types';
import flowRight from 'lodash/flowRight';
import { withRouter } from 'react-router';

import { withOverlayControls } from '@eb/eds-structure';

import ConfirmNavigationBeforeUnload from './ConfirmNavigationBeforeUnload';
import {
    handleSaveAction,
    getDialogProps,
    setAsyncRouteLeaveHook,
} from './utils';

/**
 * A Dialog wrapped with react-router functionality.
 * You can include this component in your form and it will automatically
 * wire itself to your router to handle user navigation confirmation.
 * @extends React.Component
 * @prop {object} route - the react route provided to your component by the router
 * @prop {bool} shouldConfirm - whether the user should be prompted before allowing navigation
 * @example
 * // <ConfirmNavigationDialog
 * //   shouldConfirm={this.props.dirty}
 * //   route={this.props.route}
 * // />
 */
class ConfirmNavigationDialog extends Component {
    static propTypes = {
        // This route is provided from react-router so its contents are sort of
        // a black box. We're giving it right back to react-router in the case
        // that the user decides to navigate away
        // eslint-disable-next-line react/forbid-prop-types
        router: PropTypes.object.isRequired,
        // eslint-disable-next-line react/forbid-prop-types
        route: PropTypes.object.isRequired,

        onSave: PropTypes.func,
        showOverlay: PropTypes.func,
        shouldConfirm: PropTypes.bool,
    };

    static defaultProps = {
        shouldConfirm: true,
    };

    componentDidMount() {
        const { router, route } = this.props;

        setAsyncRouteLeaveHook(router, route, this._routerWillLeave.bind(this));
    }

    _routerWillLeave() {
        const { shouldConfirm } = this.props;

        if (!shouldConfirm) {
            return null;
        }

        return new Promise((resolve) => {
            const { onSave, showOverlay } = this.props;
            const onDiscard = resolve.bind(null, true);
            const onClose = resolve.bind(null, false);
            let saveHandler;

            if (onSave) {
                saveHandler = this._handleSave.bind(this, onDiscard, onClose);
            }

            showOverlay(
                'dialog',
                getDialogProps(onDiscard, onClose, saveHandler),
            );
        });
    }

    _handleSave(onDiscard, onClose) {
        const { onSave } = this.props;

        handleSaveAction(onSave, onDiscard, onClose);
    }

    render() {
        const { shouldConfirm } = this.props;

        return <ConfirmNavigationBeforeUnload shouldConfirm={shouldConfirm} />;
    }
}

export default flowRight(
    withRouter,
    withOverlayControls,
)(ConfirmNavigationDialog);
